import type { CampaignCreationCapabilities, SponsoredBrandPlacement } from "@adpilot/shared";
import { z } from "zod";

export type AdFormat = "productCollection" | "storeSpotlight" | "video";
export type TargetingType = "keyword" | "product";
export type MatchType = "B" | "P" | "E";
export type NegativeKeywordMatchType = "negativePhrase" | "negativeExact";
export type RecommendationType = "keyword" | "product" | "category";
export type ProductCollectionLandingPage = "amazonStore" | "newLandingPage";
export type VideoLandingPage = "amazonStore" | "productDetailPage";
export type LandingPage = ProductCollectionLandingPage | VideoLandingPage;
export type AdPreviewPlacement =
  | "topSearchMobile"
  | "topSearchDesktop"
  | "searchFooterDesktop"
  | "searchFooterMobile"
  | "videoMobile"
  | "videoDesktop";
export type PlacementField = "topOfSearch" | "home" | "productPage" | "restOfSearch";

export interface KeywordRow {
  id: string;
  keyword: string;
  matchType: MatchType;
  bid: string;
  suggestedBid?: string | null;
}

export interface NegativeKeywordRow {
  id: string;
  keyword: string;
  matchType: NegativeKeywordMatchType;
}

export interface CategoryTargetRow {
  id: string;
  name: string;
}

export interface KeywordAddResult {
  added: number;
  attempted: number;
}

export interface SponsoredBrandsMoneyCapabilities {
  currencyCode: string;
  dailyBudget: CampaignCreationCapabilities["dailyBudget"];
  bid: CampaignCreationCapabilities["bid"];
}

export const FORMAT_KEYS: AdFormat[] = ["productCollection", "storeSpotlight", "video"];
export const MATCH_TYPES: MatchType[] = ["B", "P", "E"];
export const NEGATIVE_KEYWORD_MATCH_TYPES = ["negativePhrase", "negativeExact"] as const;

export const PLACEMENT_FIELDS: Array<{
  key: PlacementField;
  placement: SponsoredBrandPlacement;
}> = [
  { key: "topOfSearch", placement: "TOP_OF_SEARCH" },
  { key: "home", placement: "HOME" },
  { key: "productPage", placement: "DETAIL_PAGE" },
  { key: "restOfSearch", placement: "OTHER" },
];

export const SponsoredBrandDetailsFormSchema = z.object({
  campaignName: z.string(),
  startDate: z.string(),
  endDate: z.string(),
  budget: z.string(),
  /** Brand Registry brandId; the only brand key both sellers and vendors receive from Amazon. */
  brandId: z.string().nullable(),
  adGroupName: z.string(),
  adName: z.string(),
  brandName: z.string(),
  headline: z.string(),
  logoAssetId: z.string().nullable(),
  customImageAssetId: z.string().nullable(),
  videoAssetId: z.string().nullable(),
});
export type SponsoredBrandDetailsForm = z.infer<typeof SponsoredBrandDetailsFormSchema>;
